import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Button,
    Input,
    Card,
    CardContent
} from '@mui/material';
import { UploadFile, Download, Storage, CheckCircle } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl } from '../config/api';

function AdminSentences() {
    const { token } = useAuth();
    const [sentences, setSentences] = useState([]);
    const [isLoadingSentences, setIsLoadingSentences] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(100);
    const [totalCount, setTotalCount] = useState(0);
    const [filterId, setFilterId] = useState('');
    const [filterInput, setFilterInput] = useState('');
    const [isLoadingUpload, setIsLoadingUpload] = useState(false);
    const [isLoadingDownload, setIsLoadingDownload] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [activeFile, setActiveFile] = useState(null);

    useEffect(() => {
        const fetchSentences = async () => {
            setIsLoadingSentences(true);
            setError('');
            try {
                let url = `admin/sentences?skip=${page * rowsPerPage}&limit=${rowsPerPage}`;
                if (filterId) {
                    url += `&iteration_id=${encodeURIComponent(filterId)}`;
                }
                const response = await fetch(buildApiUrl(url), {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });
                if (!response.ok) {
                    let errorMsg = `Erro ${response.status}`;
                    try {
                        const errorData = await response.json();
                        errorMsg = errorData.detail || errorMsg;
                    } catch (e) { /* Ignore */ }
                    throw new Error(errorMsg);
                }
                const data = await response.json();
                setSentences(data.sentences || []);
                setTotalCount(data.total || 0);
            } catch (err) {
                setError(err.message);
            } finally {
                setIsLoadingSentences(false);
            }
        };
        if (token) fetchSentences();
    }, [token, page, rowsPerPage, filterId]);

    useEffect(() => {
        const fetchActiveFile = async () => {
            try {
                const response = await fetch(buildApiUrl('admin/sentence-files/active'), {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    setActiveFile(data);
                }
            } catch (err) {
                // Active file not found is ok
            }
        };
        if (token) fetchActiveFile();
    }, [token]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleFilterChange = (e) => {
        setFilterInput(e.target.value);
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        setPage(0);
        setFilterId(filterInput.trim());
    };

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
        setError('');
        setSuccess('');
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Por favor, selecione um arquivo CSV para importar.');
            return;
        }
        if (!token) {
            setError('Erro de autenticação. Faça login novamente.');
            return;
        }
        setIsLoadingUpload(true);
        setError('');
        setSuccess('');
        const formData = new FormData();
        formData.append('file', selectedFile);
        try {
            const response = await fetch(buildApiUrl('admin/sentences'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });
            const result = await response.json();
            if (!response.ok) {
                throw new Error(result.detail || `Erro ${response.status} ao importar CSV.`);
            }
            setSuccess(result.message || 'Arquivo CSV importado com sucesso!');
            setSelectedFile(null);
            document.getElementById('csv-upload-input').value = null;
            setPage(0); // Volta para primeira página após importação
            setFilterId('');
            setFilterInput('');
            // Refresh active file info after upload
            const response2 = await fetch(buildApiUrl('admin/sentence-files/active'), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (response2.ok) {
                const data = await response2.json();
                setActiveFile(data);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoadingUpload(false);
        }
    };

    const handleDownload = async () => {
        if (!token) {
            setError('Erro de autenticação. Faça login novamente.');
            return;
        }
        setIsLoadingDownload(true);
        setError('');
        setSuccess('');
        try {
            const response = await fetch(buildApiUrl('admin/sentences/export'), {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                let errorMsg = `Erro ${response.status}`;
                try {
                    const errorData = await response.json();
                    errorMsg = errorData.detail || errorMsg;
                } catch (e) { /* Ignore if not JSON */ }
                throw new Error(errorMsg);
            }
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'sentences.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            setSuccess('Download iniciado.');
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoadingDownload(false);
        }
    };

    const sortedSentences = [...sentences].sort((a, b) => {
        // Ordena numericamente pelo iteration_id, se possível
        const numA = parseInt(a.iteration_id, 10);
        const numB = parseInt(b.iteration_id, 10);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return String(a.iteration_id).localeCompare(String(b.iteration_id));
    });

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('pt-BR');
    };

    return (
        <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>
                Gerenciar Sentenças
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                Visualize, importe ou exporte sentenças. O CSV deve conter as colunas: "Iteration", "Page Title", "Random Paragraph".
            </Typography>

            {/* Active File Information */}
            {activeFile && (
                <Card sx={{ mb: 3, backgroundColor: '#e8f5e8' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CheckCircle color="success" sx={{ mr: 1 }} />
                                    Arquivo Ativo: {activeFile.filename}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Importado em {formatDate(activeFile.upload_date)} • {activeFile.sentence_count} sentenças
                                </Typography>
                                {activeFile.description && (
                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                        {activeFile.description}
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                variant="outlined"
                                startIcon={<Storage />}
                                component="a"
                                href={`${process.env.PUBLIC_URL || ''}/admin/sentence-files`}
                                size="small"
                            >
                                Gerenciar Arquivos
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}
            <form onSubmit={handleFilterSubmit} style={{ marginBottom: 16 }}>
                <TextField
                    label="Filtrar por Iteration ID"
                    value={filterInput}
                    onChange={handleFilterChange}
                    size="small"
                    sx={{ mr: 2, width: 250 }}
                />
                <Button type="submit" variant="outlined">Filtrar</Button>
            </form>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" gutterBottom>Importar Sentenças</Typography>
                    <Input
                        id="csv-upload-input"
                        type="file"
                        onChange={handleFileChange}
                        sx={{ display: 'none' }}
                        inputProps={{ accept: ".csv" }}
                    />
                    <label htmlFor="csv-upload-input">
                        <Button variant="outlined" component="span" startIcon={<UploadFile />} sx={{ mr: 1 }}>
                            Selecionar CSV
                        </Button>
                    </label>
                    {selectedFile && <Typography variant="caption" sx={{ mr: 2 }}>{selectedFile.name}</Typography>}
                    <Button
                        variant="contained"
                        onClick={handleUpload}
                        disabled={isLoadingUpload || !selectedFile}
                        startIcon={isLoadingUpload ? <CircularProgress size={20} color="inherit" /> : <UploadFile />}
                    >
                        Importar
                    </Button>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 1 }}>
                    <Button
                        onClick={handleDownload}
                        disabled={isLoadingDownload}
                        size="small"
                        sx={{ minWidth: 0, p: 1 }}
                        aria-label="Exportar Sentenças"
                    >
                        {isLoadingDownload ? <CircularProgress size={20} color="inherit" /> : <Download />}
                    </Button>
                </Box>
                <Box>
                    {isLoadingSentences ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <TableContainer component={Paper}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Iteration</TableCell>
                                            <TableCell>Page Title</TableCell>
                                            <TableCell>Random Paragraph</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedSentences.map((row, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{row.iteration_id}</TableCell>
                                                <TableCell>{row.page_title}</TableCell>
                                                <TableCell>{row.paragraph}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={totalCount}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                rowsPerPageOptions={[rowsPerPage]}
                            />
                        </>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}

export default AdminSentences;

